import {
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
  Link,
} from "@mui/material";
import React, { useRef } from "react";
import {
  CustomeDialog,
  InputControl,
  Loader,
  PageListLayout,
  UseUoMHelper,
} from "../../../shared";
import CloseIcon from "@mui/icons-material/Close";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { useIngredients } from "../hooks/UseIngredient";
import { useNavigate, useParams } from "react-router-dom";
import { useUnits } from "../../MasterData/hooks/UseUnits";
import { createColumnHelper } from "@tanstack/react-table";
import SpecsheetSection from "../components/SpecsheetSection";
import BasicDetailsSection from "../components/BasicDetailsSection";
import AlternativeUoMSection from "../components/AlternativeUoMSection";
import CostPerUnitSection from "../components/CostPerUnitSection";
import UnabledIngredientUnitDialog from "../components/UnabledIngredientUnitDialog";
import { VariantSection } from "../components/VariantSection";
import { PosScheduleSyncModal } from "../../Recipe/components/PosScheduleSyncModal";

export const AddOrEditIngredient = () => {
  const { id } = useParams();
  const {
    ingredient,
    onChange,
    validator,
    optionsTaxGroup,
    optionsTypes,
    updateIngredient,
    createIngredient,
    onTableChange,
    handleAddUnits,
    handleDelete,
    onSubmit,
    onTablehandleChange,
    handleProductInputChange,
    onDeleteTagSelect,
    onChangeTax,
    handleDropzonePdf,
    state,
    createPdfUrl,
    handleDeletePdf,
    unitValidator,
    handleSupplierInputChange,
    optionsTags,
    optionsSupplier,
    onDeleteSupplierSelect,
    handleDialogClose,
    onVariantChange,
    onVariantAddRemove,
    handleSyncModal,
    handleScheduledChange,
    handleOnSubmit,
  } = useIngredients({
    detId: id,
    altunitlist: true,
    isIngredientlist: false,
    isTaxgrouplist: true,
    isTaglist: true,
  });
  const { getUniqueUnitIds, filterUnits, filterAlternativeUnits } =
    UseUoMHelper();
  const addpdfHandler = () => {
    pdfRef.current.click();
  };
  const navigate = useNavigate();
  const filter = createFilterOptions();

  const { listQueryData } = useUnits({ list: true });

  const pdfRef = useRef();
  const optionsUnits =
    listQueryData?.data
      ?.filter((i) => i?.ignore === 0)
      ?.map((item) => ({
        name: item.symbol,
        id: item.id,
      })) || [];
  const unitIds = getUniqueUnitIds(ingredient.data.units);
  let buyingUnits = ingredient.data.has_uom
    ? optionsUnits?.filter((item) => unitIds.includes(item.id))
    : optionsUnits?.filter((item) => item?.name === "kg");
  buyingUnits.push(optionsUnits.find((item) => item?.name === "gm"));
  const useUomUnits = buyingUnits?.filter(
    (unit) => unit?.name !== "kg" && unit?.name !== "gm",
  );

  const breadcrumbs = [
    <Link
      underline="none"
      key="1"
      color="#1760D2"
      onClick={(e) => navigate("/ingredients")}
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      Ingredients
    </Link>,
    <Typography
      key="2"
      color="inherit"
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      {id ? ingredient?.data?.name : "Create New Ingredient"}
    </Typography>,
  ];

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor("pdf", {
      header: "Spec Sheet",
      enableSorting: false,
      cell: (info) => {
        const pdfUrl = info?.row?.original.doc_name;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: { md: "500px", xs: "100%" },
            }}
          >
            <img src="/icons/ic_file.jpg" alt="ic_file" />
            <Typography
              variant="body1"
              sx={{
                whiteSpace: "nowrap",
                flexGrow: 1,
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
            >
              {pdfUrl}
            </Typography>
          </Box>
        );
      },
    }),

    columnHelper.accessor("id", {
      header: "",
      align: "right",
      meta: { width: "80px" },
      cell: (info) => {
        return (
          <Button
            type="button"
            size="small"
            variant="text"
            color="error"
            onClick={() => handleDeletePdf(info.row.index)}
          >
            remove
          </Button>
        );
      },
    }),
  ];

  const columns2 = [
    {
      header: "Base Qty",
      meta: { width: "150px" },
      cell: (row, index) => {
        return (
          <InputControl
            name="base_unit_qty"
            placeholder="Base Qty"
            type="text"
            size="small"
            fullWidth
            error={unitValidator.current.message(
              `Base Qty ${index}`,
              row.base_unit_qty,
              "required|customNumeric",
            )}
            helperText={unitValidator.current.message(
              `Base Qty ${index}`,
              row.base_unit_qty,
              "required|customNumeric",
            )}
            onChange={(e) => onTablehandleChange(e, index)}
            value={row.base_unit_qty}
            disabled={ingredient.data.units.length - 1 !== index}
          />
        );
      },
    },
    {
      header: "Base Unit",
      meta: { minWidth: "150px" },
      cell: (row, index) => {
        const unitIds = getUniqueUnitIds(ingredient.data.units);
        const baseUnitOptions = filterUnits(listQueryData?.data, [
          "kg",
          "ltr",
          "ml",
        ]).filter((i) => unitIds.includes(i.id));
        return (
          <InputControl
            type="dropdown"
            fullWidth
            options={baseUnitOptions ?? []}
            disabled={index < 1 || ingredient.data.units.length - 1 !== index}
            getOptionLabel={(option) => option?.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={
              (baseUnitOptions.length > 0 &&
                row.base_unit_id &&
                baseUnitOptions.find((c) => c.id === row.base_unit_id)) ||
              null
            }
            onChange={(e, newValue) =>
              onTableChange(
                {
                  target: {
                    name: "base_unit_id",
                    value: newValue?.id,
                  },
                },
                "base_unit_id",
                index,
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Unit"
                size="small"
                error={unitValidator.current.message(
                  `Base Unit ${index}`,
                  row.base_unit_id,
                  "required",
                )}
                helperText={unitValidator.current.message(
                  `Base Unit ${index}`,
                  row.base_unit_id,
                  "required",
                )}
              />
            )}
          />
        );
      },
    },
    {
      header: "=",
      cell: (info) => "=",
    },
    {
      header: "Alternative Qty",
      meta: { width: "120px" },
      cell: (row, index) => {
        return (
          <InputControl
            type="text"
            size="small"
            name="alt_unit_qty"
            placeholder="Alt Qty"
            fullWidth
            error={unitValidator.current.message(
              `Alt Qty ${index}`,
              row.alt_unit_qty,
              "required|customNumeric",
            )}
            helperText={unitValidator.current.message(
              `Alt Qty ${index}`,
              row.alt_unit_qty,
              "required|customNumeric",
            )}
            onChange={(e) => onTablehandleChange(e, index)}
            value={row.alt_unit_qty}
            disabled={ingredient.data.units.length - 1 !== index}
          />
        );
      },
    },
    {
      header: "Alternative Unit",
      meta: { minWidth: "200px" },
      cell: (row, index) => {
        let altUnitOptions = filterAlternativeUnits(
          filterUnits(listQueryData?.data, ["ltr", "ml"]),
          index,
          ingredient.data.units,
        );
        const mlId = listQueryData?.data?.find((i) => i.symbol === "ml")?.id;
        const ltrId = listQueryData?.data?.find((i) => i.symbol === "ltr")?.id;
        const hasMl = ingredient.data.units.some(
          (item, i) => i !== index && item.alt_unit_id === mlId,
        );
        const hasLtr = ingredient.data.units.some(
          (item, i) => i !== index && item.alt_unit_id === ltrId,
        );
        if (hasLtr) {
          altUnitOptions = altUnitOptions.filter((i) => i.id !== mlId);
        } else if (hasMl) {
          altUnitOptions = altUnitOptions.filter((i) => i.id !== ltrId);
        }

        return (
          <InputControl
            type="dropdown"
            fullWidth
            disabled={ingredient.data.units.length - 1 !== index}
            options={altUnitOptions ?? []}
            getOptionLabel={(option) => option?.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={
              (altUnitOptions?.length > 0 &&
                row.alt_unit_id &&
                altUnitOptions?.find((c) => c.id === row.alt_unit_id)) ||
              null
            }
            onChange={(e, newValue) =>
              onTableChange(
                {
                  target: {
                    name: "alt_unit_id",
                    value: newValue?.id,
                  },
                },
                "alt_unit_id",
                index,
                newValue,
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Unit"
                size="small"
                error={unitValidator.current.message(
                  `Alt Unit ${index}`,
                  row?.alt_unit_id,
                  "required",
                )}
                helperText={unitValidator.current.message(
                  `Alt Unit ${index}`,
                  row?.alt_unit_id,
                  "required",
                )}
              />
            )}
          />
        );
      },
    },
    {
      header: "",
      meta: { textAlign: "right", width: "36px" },
      cell: (row, index) => {
        return (
          <RemoveCircleIcon
            onClick={() => {
              index === ingredient?.data?.units.length - 1 &&
                index !== 0 &&
                handleDelete(row.base_unit_qty, index);
            }}
            sx={{
              width: "20px",
              height: "20px",
              color:
                index === ingredient?.data?.units.length - 1 && index !== 0
                  ? "#B42318"
                  : "#BCC7DB",
              cursor:
                index === ingredient?.data?.units.length - 1 && index !== 0
                  ? "pointer"
                  : "",
            }}
          />
        );
      },
    },
  ];

  return (
    <>
      {validator.current.purgeFields()}
      {unitValidator.current.purgeFields()}
      <PageListLayout
        breadcrumbs={breadcrumbs}
        headerText={id ? ingredient?.data?.name : "Create New Ingredient"}
        isChanged={false}
        rightContent={
          <Stack direction="row" gap="8px">
            <Button
              type="button"
              size="small"
              variant="contained"
              color="inherit"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="small"
              variant="contained"
              onClick={onSubmit}
              disabled={
                updateIngredient.isPending || createIngredient.isPending
              }
            >
              {id ? "Update" : "Save"}
            </Button>
          </Stack>
        }
        sx={{ backgroundColor: "#ffffff", p: 0, px: "20px" }}
      >
        {listQueryData?.isLoading ? (
          <Loader />
        ) : (
          <>
            <BasicDetailsSection
              ingredient={ingredient}
              validator={validator}
              onChange={onChange}
              onChangeTax={onChangeTax}
              optionsTaxGroup={optionsTaxGroup}
              optionsTypes={optionsTypes}
              optionsTags={optionsTags}
              handleProductInputChange={handleProductInputChange}
              onDeleteTagSelect={onDeleteTagSelect}
            />

            <Grid
              container
              gap="24px"
              py="24px"
              borderBottom="1px solid"
              borderColor="divider"
            >
              <Grid item sm={4}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ width: "150px" }}
                >
                  Suppliers
                </Typography>
              </Grid>
              <Grid item flex={1}>
                <Stack
                  sx={{
                    gap: "18px",
                  }}
                >
                  <InputControl
                    value={ingredient?.data?.suppliers}
                    type="dropdown"
                    name="suppliers"
                    label="suppliers"
                    multiple
                    className="tags-input"
                    isOptionEqualToValue={(option, value) => option === value}
                    onChange={(event, newValue) => {
                      if (typeof newValue === "string") {
                        handleSupplierInputChange({
                          target: {
                            name: "suppliers",
                            value: newValue,
                          },
                        });
                      } else if (newValue && newValue.inputValue) {
                        // Create a new value from the user input
                        handleSupplierInputChange({
                          target: {
                            name: "suppliers",
                            value: newValue.inputValue,
                          },
                        });
                      } else {
                        handleSupplierInputChange({
                          target: {
                            name: "suppliers",
                            value: newValue,
                          },
                        });
                      }
                    }}
                    options={optionsSupplier ?? []}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      const { inputValue } = params;
                      // Suggest the creation of a new value
                      const isExisting = options.some(
                        (option) => inputValue === option,
                      );
                      if (inputValue !== "" && !isExisting) {
                        filtered.push(inputValue);
                      }
                      return filtered;
                    }}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    id="free-solo-with-text-demo"
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => <></>)
                    }
                    getOptionLabel={(option) => {
                      // Value selected with enter, right from the input
                      if (typeof option === "string") {
                        return option;
                      }
                      // Add "xxx" option created dynamically
                      if (option.inputValue) {
                        return option.inputValue;
                      }
                      // Regular option
                      return option;
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>{option}</li>
                    )}
                    freeSolo
                    renderInput={(params) => (
                      <TextField {...params} label="Type or Select Supplier" />
                    )}
                    sx={{ width: { md: "446px", xs: "100%" } }}
                  />
                  <Stack
                    sx={{
                      flexDirection: "row",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "8px",
                    }}
                  >
                    {ingredient?.data?.suppliers?.map((data, index) => (
                      <Chip
                        variant="outlined"
                        color="gray"
                        label={data}
                        deleteIcon={<CloseIcon />}
                        onDelete={() => {
                          onDeleteSupplierSelect(data);
                        }}
                        sx={{
                          width: "115px",
                          "& .MuiChip-deleteIcon": {
                            color: "#CCCCCC",
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Stack>
              </Grid>
            </Grid>

            {ingredient?.data?.type !== "stocktake" && (
              <VariantSection
                ingredient={ingredient}
                onChange={onVariantChange}
                onVariantAddRemove={onVariantAddRemove}
                validator={validator}
              />
            )}

            <AlternativeUoMSection
              ingredient={ingredient}
              onChange={onChange}
              handleAddUnits={handleAddUnits}
              columns={columns2}
            />

            <Grid
              container
              gap="24px"
              py="24px"
              borderBottom="1px solid"
              borderColor="divider"
            >
              <Grid item sm={4}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ width: "150px" }}
                >
                  Use UoM?
                </Typography>
                <Typography fontWeight={400} fontSize={"14px"} color="#657083">
                  Use alternative units in charts/templates for display.
                </Typography>
              </Grid>
              <Grid item flex={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    gap: "12px",
                  }}
                >
                  <InputControl
                    type="switch"
                    name="use_uom"
                    onChange={(e) => {
                      onChange({
                        target: {
                          name: "use_uom",
                          value: e.target.checked ? 1 : 0,
                        },
                      });
                    }}
                    checked={ingredient?.data?.use_uom}
                    disabled={
                      ingredient.data.has_uom &&
                      ingredient?.data?.units[0]?.alt_unit_id
                        ? false
                        : true
                    }
                  />
                  {ingredient?.data?.use_uom && ingredient?.data?.has_uom ? (
                    <InputControl
                      label="UoM"
                      required
                      sx={{ width: { md: "292px", xs: "100%" } }}
                      type="dropdown"
                      disableClearable
                      options={useUomUnits ?? []}
                      getOptionLabel={(option) => option?.name}
                      value={
                        (useUomUnits?.length > 0 &&
                          ingredient?.data?.use_uom_unit &&
                          useUomUnits?.find(
                            (c) => c?.id === ingredient?.data?.use_uom_unit,
                          )) ||
                        null
                      }
                      onChange={(e, newValue) => {
                        onChange({
                          target: {
                            name: "use_uom_unit",
                            value: newValue?.id,
                          },
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="UoM"
                          required
                          error={validator.current.message(
                            "use uom unit",
                            ingredient?.data?.use_uom_unit,
                            "required",
                          )}
                          helperText={validator.current.message(
                            "use uom unit",
                            ingredient?.data?.use_uom_unit,
                            "required",
                          )}
                        />
                      )}
                    />
                  ) : null}
                </Stack>
              </Grid>
            </Grid>

            <CostPerUnitSection
              ingredient={ingredient}
              buyingUnits={buyingUnits}
              onChange={onChange}
              validator={validator}
            />

            <SpecsheetSection
              state={state}
              columns={columns}
              pdfRef={pdfRef}
              addPdfHandler={addpdfHandler}
              handleDropzonePdf={handleDropzonePdf}
              createPdfUrl={createPdfUrl}
            />

            {ingredient?.data?.type !== "stocktake" && (
              <Grid
                container
                gap="24px"
                py="24px"
                borderBottom="1px solid"
                borderColor="divider"
              >
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ width: "150px" }}
                  >
                    Notes
                  </Typography>
                </Grid>
                <Grid item flex={1}>
                  <Stack width={"100%"} gap="15px">
                    <InputControl
                      name="notes"
                      onChange={onChange}
                      value={ingredient?.data?.notes}
                      type="text"
                      label="Add Notes here"
                      placeholder="Add Notes here"
                      multiline
                      fullWidth
                      minRows={4}
                      variant="outlined"
                      sx={{
                        borderRadius: "8px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            )}
          </>
        )}

        <CustomeDialog
          open={ingredient?.isErrorMessage}
          title={
            <Stack direction="row" alignItems="center" gap="12px" p="16px">
              <img
                alt="pairIcon"
                src="/icons/ic_pair_dialog.svg"
                height="36px"
                width="36px"
              />
              <Typography fontWeight={500} variant="body1" fontSize={20}>
                {!ingredient?.errorTypes.includes("units") &&
                ingredient?.errorTypes.includes("status")
                  ? "Unable to Change Status"
                  : "Unable to Remove Unit"}
              </Typography>
            </Stack>
          }
          sx={{ width: { sm: "500px", sx: "90%" } }}
          content={
            <UnabledIngredientUnitDialog
              state={ingredient}
              handleClose={handleDialogClose}
            />
          }
          footer={{
            display: true,
            cancelButton: (
              <Button
                size="small"
                variant="outlined"
                color="gray"
                onClick={handleDialogClose}
              >
                Close
              </Button>
            ),
          }}
          divider={false}
        />

        <PosScheduleSyncModal
          open={ingredient.isOpenSyncModal}
          onClose={() => handleSyncModal(false)}
          isBtnLoading={updateIngredient.isPending}
          onSave={() => handleOnSubmit("schedule")}
          syncType={ingredient?.data?.sync_type}
          setSyncType={(type) =>
            handleScheduledChange({
              target: {
                name: "sync_type",
                value: type,
              },
            })
          }
          scheduledDate={ingredient?.data?.scheduled_date}
          setScheduledDate={(date) =>
            handleScheduledChange({
              target: {
                name: "scheduled_date",
                value: date,
              },
            })
          }
        />
      </PageListLayout>
    </>
  );
};
